type CustomerPermissions = {
  canView?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canMutate?: boolean;
};

type CrudPermissions = {
  canView?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
};

type CashupReportPermissions = CrudPermissions & {
  /** @deprecated Rollout bridge for legacy POS clients. Use canCreate/canEdit/canDelete. */
  canMutate?: boolean;
};

export type PermissionAction = 'canView' | 'canMutate' | 'canCreate' | 'canEdit' | 'canDelete';

type SectionPermissions = Partial<Record<PermissionAction, boolean>>;
const cashupReportMutationActions: PermissionAction[] = ['canCreate', 'canEdit', 'canDelete'];

const getPermissions = (user: any): Record<string, SectionPermissions> | undefined => {
  return user?.permission ?? user?.permissions;
};

export const hasPermission = (
  user: any,
  section: string,
  action: PermissionAction
): boolean => {
  const sectionPermissions = getPermissions(user)?.[section];
  if (!sectionPermissions) return false;

  if (sectionPermissions[action] !== undefined) {
    return Boolean(sectionPermissions[action]);
  }

  if (section === 'customers' && (action === 'canCreate' || action === 'canEdit')) {
    return Boolean(sectionPermissions.canMutate);
  }

  if (section === 'cashupReports' && cashupReportMutationActions.includes(action)) {
    return Boolean(sectionPermissions.canMutate);
  }

  return false;
};

export const canViewFinancialSummary = (user: unknown): boolean => {
  const permissions = getPermissions(user);
  return hasPermission(user, 'reports', 'canView') &&
    hasPermission(user, 'expenses', 'canView') &&
    permissions?.hideOrderTotals?.canView === false &&
    permissions?.hideCashupTotals?.canView === true;
};

export const normalizeCustomersPermissionsForWrite = (
  input?: CustomerPermissions | null
): Pick<CustomerPermissions, 'canView' | 'canCreate' | 'canEdit'> => {
  return {
    canView: input?.canView ?? true,
    canCreate: input?.canCreate ?? input?.canMutate ?? true,
    canEdit: input?.canEdit ?? input?.canMutate ?? true
  };
};

export const normalizeCrudPermissionsForWrite = (
  input?: CrudPermissions | null
): Required<CrudPermissions> => ({
  canView: input?.canView === true,
  canCreate: input?.canCreate === true,
  canEdit: input?.canEdit === true,
  canDelete: input?.canDelete === true
});

export const normalizeExpensesPermissionsForWrite = normalizeCrudPermissionsForWrite;

export const normalizeCashupReportsPermissionsForWrite = (
  input?: CashupReportPermissions | null
): Required<CashupReportPermissions> => {
  const normalized = normalizeCrudPermissionsForWrite(input);
  const canMutate = normalized.canCreate || normalized.canEdit || normalized.canDelete;

  return {
    ...normalized,
    canMutate
  };
};

export const normalizePermissionsForWrite = <TPermission extends Record<string, any>>(
  permission: TPermission
): TPermission => {
  if (!permission) return permission;

  return {
    ...permission,
    assignOrderLineEmployees: {
      canMutate: permission.assignOrderLineEmployees?.canMutate === true
    },
    customers: normalizeCustomersPermissionsForWrite(permission.customers),
    expenses: normalizeCrudPermissionsForWrite(permission.expenses),
    cashupReports: normalizeCashupReportsPermissionsForWrite(permission.cashupReports)
  };
};
